import * as React from "react";
import { useMemo } from "react";
import moment from "moment";

import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { AssessmentReport } from "../../reports/AssessmentReport";
import { divisions } from "../../utils/divisionConfig";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Checkbox,
  Menu,
  FormGroup,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useAxios } from "../../hooks/useAxios";
import { AxiosError } from "axios";
import { ApiResponse } from "../../models/ApiResponse";
import { showToast } from "../../components/Toaster";
import {
  emptyServiceCall,
  ServiceCall,
  ServiceCallLog,
  serviceCallLogActionDescription,
  SERVICE_CALL_DIVISION_ENUM,
  SERVICE_CALL_STATUS_ENUM,
  SERVICE_CALL_TYPE_ENUM,
  PRIORITY_LEVEL_ENUM,
  PHYSICAL_CONDITION_ENUM,
  CallItem,
} from "../../models/ServiceCall";
import { useDataProvider } from "../../hooks/useDataProvider";
import { TabPanel } from "../../components/TabPanel";
import CallItemsControl from "../../components/CallItemsControl";
import NotesComponent from "../shared/NotesComponent";
import MeterCountComponent from "../shared/MeterCountComponent";
import { Machine, emptyMachine } from "../../models/Machine";
import { Customer, emptyCustomer } from "../../models/Customers";
import PrintCallModal, { printCallModalOpen } from "../shared/PrintCallModal";
import { emptyUser } from "../../models/User";
import { UtcToPst } from "../../utils/UtcToPst";
import { formatToISODate } from "../../utils/formatToISODate";
import { formatDateTime, formatToLocalInput } from "../../utils/dateUtils";
import { RepairReport } from "../../reports/RepairCompletionReport";
import { WorkOrderReport } from "../../reports/WorkOrderReport";

// import EmailTools from '../../Lib/Static/EmailTools';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  root: {
    "@media print": {
      position: "absolute",
    },
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

const BootstrapDialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

let serviceCallModalOpen = (call: ServiceCall) => {};
let serviceCallModalClose = () => {};

type UserDialogProps = {
  onClose?: () => void;
};

export default function CustomizedDialogs({ onClose }: UserDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [_sc, setSc] = React.useState<ServiceCall>(emptyServiceCall());
  const [loading, setLoading] = React.useState(false);
  const [isAdd, setIsAdd] = React.useState(false);
  const [machineContract, setMachineContract] = React.useState(false);
  const [machineSelectOpen, setMachineSelectOpen] = React.useState(false);
  const [history, setHistory] = React.useState<ServiceCallLog[]>([]);
  const [holdWarn, setHoldWarn] = React.useState(false);
  const [affectedMachine, setAffectedMachine] = React.useState<
    Machine | undefined
  >(undefined);
  const [customerDivision, setCustomerDivision] = React.useState(0);
  const [selectedCustomer, setSelectedCustomer] =
    React.useState<Customer>(emptyCustomer());
  const [customerId, setCustomerId] = React.useState<string>("");
  const [equipmentIdFilter, setEquipmentIdFilter] = React.useState<string>("");
  const [customerOptions, setCustomerOptions] = React.useState<
    readonly Customer[]
  >([]);
  const [InStoreRepair, setInStoreRepair] = React.useState(false);
  const [repairCompleted, setRepairCompleted] = React.useState(false);
  const [items, setItems] = React.useState<CallItem[] | undefined>(undefined);

  const axios = useAxios();

  const data = useDataProvider();

  useMemo(() => {
    if (data.customers) {
      setCustomerOptions(
        data.customers?.filter((c) => c.division === customerDivision),
      );
    }
  }, [data.customers, customerDivision]);

  const [tabValue, setTabValue] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Modified by alvin
  React.useEffect(() => {
    if (customerId !== "" && data.customers) {
      setHoldWarn(false);
      const selected = data.customers.find((c) => c.id === customerId);
      if (selected) {
        if (selected.hold) {
          setHoldWarn(true);
        }
        setSelectedCustomer(selected);
        setEquipmentIdFilter("");
      }
    } else {
      // initialize customer object
      setSelectedCustomer(emptyCustomer());
      console.log("Customer Initial value - Empty");
    }
  }, [customerId, data.customers]);

  const isNullOrWhiteSpace = (s: any): boolean => {
    return (
      s === undefined || s === null || s === "" || (s as string).trim() === ""
    );
  };

  const fetchItems = React.useCallback(
    async (id?: string) => {
      if (!id) return;

      try {
        setLoading(true);

        const response = await axios.get<ApiResponse<CallItem[]>>(
          `callitem/get?id=${id}`,
        );

        if (response.data.success) {
          setItems(response.data.data);
        }
      } catch (error) {
        console.error("Failed loading items", error);
      } finally {
        setLoading(false);
      }
    },
    [axios],
  );

  serviceCallModalOpen = (call: ServiceCall) => {
    setTabValue(0);
    // setSc(call);
    setEquipmentIdFilter("");
    setHoldWarn(false);
    setMachineContract(false);
    setHistory([]);

    setIsAdd(call.id === undefined);

    if (call.machine_id) {
      const machine = data.machines?.find((m) => m.id === call.machine_id);
      if (machine) {
        setCustomerId(machine.customer_id);
        setMachineContract(machine.contract);
        setAffectedMachine(machine);
      }
    } else {
      setCustomerId("");
    }

    const formattedCall = {
      ...call,
      date_scheduled: call.date_scheduled
        ? formatToLocalInput(call.date_scheduled, "date") // formatToISODate(call.date_scheduled)
        : undefined,
      date_received: call.created
        ? formatToLocalInput(call.created, "datetime")
        : formatToLocalInput(new Date().toISOString(), "datetime"),
    };
    setSc(formattedCall);

    // added by alvin june 2026
    setInStoreRepair(call.call_type === 5);
    setRepairCompleted(call.status === 8 || call.status === 17);
    console.log("Status", call.status);

    if (call.id) {
      axios
        .get<ApiResponse<ServiceCallLog[]>>(`calls/logs?id=${call?.id}`)
        .then((res) => {
          const api = res.data;
          setHistory(api.data);
        });

      fetchItems(call.id);
    }

    setOpen(true);
  };

  serviceCallModalClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const machineOptions = useMemo(() => {
    let results = data.machines;

    if (customerId !== "") {
      results = results?.filter((m) => m.customer_id === customerId);
    }

    if (equipmentIdFilter !== "") {
      const rgx = new RegExp(equipmentIdFilter, "i");
      results = results?.filter((m) => rgx.test(m.external_id));
    }

    return results;
  }, [data.machines, customerId, equipmentIdFilter]);

  const usersWithUnassigned = [
    { id: "unassigned", name: "<Unassigned>" }, // Unassigned item
  ];

  // const assignees = data.users?.filter((u) => u.access_level === 0) || []; // Filtered tech users
  const assignees =
    data.users
      ?.filter((u) => u.access_level === 0 && u.active === true)
      .sort((a, b) => {
        const aLetter = /^[A-Za-z]/.test(a.name);
        const bLetter = /^[A-Za-z]/.test(b.name);

        if (aLetter !== bLetter) {
          return aLetter ? -1 : 1;
        }

        return a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }) || [];

  // added by alvin june2026
  const detailIndex = 0;
  const repairIndex = 1;
  const historyIndex = 2; // InStoreRepair ? 2 : 1;
  const activityIndex = 3; //InStoreRepair ? 3 : 2;

  const accessoryOptions: string[] = [
    "Power Cord",
    "USB Cable",
    "Network Cable",
    "Telephone/Fax Cable",
    "Black Toner Cartridge",
    "Color Toner Cartridge(s)",
    "Drum Unit",
    "Waste Toner Container",
    "Paper Tray",
    "ADF Tray",
    "Document Cover",
    "HDD/SSD",
    "User Manual",
    "Driver CD/DVD",
    "Original Box",
    "Other",
  ];

  const handleAccessoryChange = (item: string) => {
    const selected = _sc.accessories
      ? _sc.accessories.split(",").map((x) => x.trim())
      : [];

    const updated = selected.includes(item)
      ? selected.filter((x) => x !== item)
      : [...selected, item];

    setSc((prev) => ({
      ...prev,
      accessories: updated.join(", "),
    }));
  };

  const getDivision = divisions[_sc.division ?? 0];
  const techName =
    data.users?.find((u) => u.id === _sc.assigned_to)?.name || "Unassigned";

  const rptData = {
    logoPath: getDivision.logo,
    divisionName: getDivision.name,
    divisionAddress: getDivision.address,
    divisionTelno: getDivision.telno,

    workOrderNo: _sc.external_id,
    dateReceived: formatDateTime(_sc.created),
    appUser: _sc.created_by?.name,

    customerName: selectedCustomer.contact_name,
    companyName: selectedCustomer.name,
    contactNo: selectedCustomer.contact_number,
    email: selectedCustomer.contact_email,

    equipmentType: affectedMachine?.brand,
    model: affectedMachine?.model,
    serialNumber: affectedMachine?.serial,
    // totalMeterCount:
    //   affectedMachine?.meter_counts?.reduce(
    //     (total, item) => total + (item.count ?? 0),
    //     0,
    //   ) ?? 0,
    meterCountMono:
      affectedMachine?.meter_counts?.find((m) => m.type === 0)?.count ?? 0,
    meterCountColor:
      affectedMachine?.meter_counts?.find((m) => m.type === 1)?.count ?? 0,
    totalMeterCount:
      (affectedMachine?.meter_counts?.find((m) => m.type === 0)?.count ?? 0) +
      (affectedMachine?.meter_counts?.find((m) => m.type === 1)?.count ?? 0),
    accessories: _sc.accessories,

    complaint: _sc.description,
    physicalCondition: _sc.physical_condition,
    initialFindings: _sc.initial_findings,
    remarks: _sc.remarks,

    technicalDiagnosis: _sc.technical_diagnosis,
    partsReplaced: _sc.parts_replaced,
    correctiveActionTaken: _sc.corrective_action_taken,
    serviceTech: techName,
    completionDate: formatDateTime(_sc.date_repaired),

    items: items,
  };

  const handlePrintAssessment = () => {
    AssessmentReport(rptData);
  };

  const handlePrintRepair = () => {
    RepairReport(rptData);
  };

  const handlePrintSWO = () => {
    WorkOrderReport(rptData);
  };

  return (
    <div>
      <BootstrapDialog
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            return false;
          }
        }}
        aria-labelledby="customized-dialog-title"
        open={open}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "800px", // Set your width here
            },
          },
        }}
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={serviceCallModalClose}
        >
          {isAdd ? "Add" : "Edit"} Service Call
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <Tabs value={tabValue} onChange={handleChange} variant="fullWidth">
            <Tab label="Call Details" />
            <Tab label="Assessment" />
            <Tab label="Call History" />
            <Tab label="Activity" />
          </Tabs>
          {/* Tab Call Details */}
          <TabPanel value={tabValue} index={detailIndex}>
            <Box
              component="form"
              sx={{
                "& > :not(style)": { mt: 1 },
              }}
              noValidate
              autoComplete="off"
            >
              {holdWarn && (
                <>
                  <Alert severity="warning">
                    Customer has a <b>HOLD STATUS</b> on the account.
                  </Alert>
                </>
              )}
              {machineContract && (
                <>
                  <Alert severity="success">
                    Selected Machine is under <b>CONTRACT</b>.
                  </Alert>
                </>
              )}
              <Grid container spacing={1} width={"100%"}>
                <Grid item sm={6}>
                  <InputLabel id="call-division">Division</InputLabel>
                  <Select
                    labelId="call-division"
                    value={_sc.division}
                    fullWidth
                    size="small"
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        division: e.target.value as number,
                      });

                      setCustomerDivision(e.target.value as number);
                    }}
                  >
                    {SERVICE_CALL_DIVISION_ENUM.map((e, i) => (
                      <MenuItem key={i} value={i}>
                        {e}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item sm={6}>
                  <InputLabel>PO Number</InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    value={_sc.po_number}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        po_number: e.target.value,
                      });
                    }}
                  />
                </Grid>

                {/* {!isAdd && <Grid item sm={6}></Grid>} */}

                {isAdd && (
                  <>
                    <Grid item sm={6}></Grid>
                    <Grid item sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Find by Equipment id"
                        value={equipmentIdFilter}
                        onChange={(e) => {
                          setEquipmentIdFilter(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setMachineSelectOpen(true);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item sm={2}>
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setEquipmentIdFilter("");
                        }}
                        sx={{
                          height: 38,
                          borderRadius: "999px",
                          textTransform: "none",
                          transition: "0.2s",
                          "&:hover": {
                            boxShadow: 2,
                          },
                        }}
                      >
                        Clear
                      </Button>
                    </Grid>
                  </>
                )}

                {/* {isAdd && (
                  <Grid item sm={6}>
                    <InputLabel id="find-by-equipment-id">.</InputLabel>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Find by Equipment id"
                          value={equipmentIdFilter}
                          onChange={(e) => {
                            setEquipmentIdFilter(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              setMachineSelectOpen(true);
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          onClick={() => {
                            setEquipmentIdFilter("");
                          }}
                          sx={{
                            height: 38,
                            borderRadius: "999px",
                            textTransform: "none",
                            transition: "0.2s",
                            "&:hover": {
                              boxShadow: 2,
                            },
                          }}
                        >
                          Clear
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                )} */}

                <Grid item sm={6}>
                  <InputLabel>Caller</InputLabel>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    value={_sc.caller}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        caller: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>
                <Grid item sm={6}>
                  <InputLabel>Caller Contact</InputLabel>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    value={_sc.caller_details}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        caller_details: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>
                <Grid item sm={6}>
                  <InputLabel>Onsite Location</InputLabel>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    value={_sc.location}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        location: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>
                <Grid item sm={6}>
                  <InputLabel>Business Hours</InputLabel>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    value={_sc.business_hours}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        business_hours: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>
                <Grid item sm={12}>
                  {/* <InputLabel id="call-machine-label">Customer</InputLabel> */}
                  <Autocomplete
                    sx={{ mt: 2 }}
                    disablePortal
                    // id="combo-box-demo"
                    id="auto-highlight"
                    autoHighlight
                    options={customerOptions}
                    value={selectedCustomer}
                    fullWidth
                    size="small"
                    getOptionLabel={(option) => option.name}
                    // isOptionEqualToValue={(option: Customer, value: Customer) => option.id === value.id}
                    // solution to resolve the duplicate list options
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Customer" />
                    )}
                    onChange={(_, newValue) => {
                      setCustomerId(newValue?.id || "");
                      setSelectedCustomer(newValue as Customer); // added by art
                      console.log("Selected Customer - " + newValue?.name);
                    }}
                  />
                </Grid>
                <Grid item sm={12}>
                  <InputLabel id="call-machine-label">Machine</InputLabel>
                  <Select
                    labelId="call-machine-label"
                    value={_sc.machine_id}
                    fullWidth
                    size="small"
                    open={machineSelectOpen}
                    onClose={() => setMachineSelectOpen(false)}
                    onOpen={() => setMachineSelectOpen(true)}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        machine_id: e.target.value,
                      });

                      const machine = data.machines?.find(
                        (m) => m.id === e.target.value,
                      );

                      if (machine) {
                        setMachineContract(machine.contract);
                      }

                      const customer = data.customers?.find(
                        (m) => m.id === machine?.customer_id,
                      );
                      console.log(customer);
                      setCustomerId(customer?.id || "");
                    }}
                  >
                    {machineOptions?.map((mac) => (
                      <MenuItem value={mac.id} key={mac.id}>
                        <div>
                          {mac.contract ? (
                            <Chip
                              // sx={{ ml: 1 }}
                              size="small"
                              color="warning"
                              label="On Contract"
                            />
                          ) : (
                            <></>
                          )}
                          <br />
                          <b>{mac.model}</b>{" "}
                          {mac.external_id !== "" &&
                          mac.external_id !== null &&
                          mac.external_id !== undefined ? (
                            <>
                              <i>
                                <u>Equipment ID:</u>
                              </i>{" "}
                              {mac.external_id}
                            </>
                          ) : (
                            <></>
                          )}
                          <br />
                          <i>{mac.serial}</i>
                          <br />
                          Contract Level & Expiration:{" "}
                          {`${mac.contract_level} (${formatToISODate(mac.expiry_date)})`}
                          <br />
                          address:{" "}
                          {`${mac.address}, ${mac.city}, ${mac.state} ${mac.zip}`}
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item sm={12}>
                  <InputLabel id="description-label">
                    Description/Concern
                  </InputLabel>
                  <TextField
                    multiline
                    rows={5}
                    fullWidth
                    size="small"
                    value={_sc.description}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        description: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>

                <Grid item sm={4}>
                  <InputLabel id="call-type">Call Type</InputLabel>
                  <Select
                    labelId="call-type"
                    value={_sc.call_type || 0}
                    fullWidth
                    size="small"
                    onChange={(e) => {
                      const callType = e.target.value as number;
                      setSc({
                        ..._sc,
                        call_type: callType,
                      });
                      setInStoreRepair(callType === 5);
                    }}
                  >
                    {SERVICE_CALL_TYPE_ENUM.map((e, i) => (
                      <MenuItem key={i} value={i}>
                        {e}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item sm={4}>
                  <InputLabel id="call-status">Status</InputLabel>
                  <Select
                    labelId="call-status"
                    value={_sc.status}
                    fullWidth
                    size="small"
                    onChange={(e) => {
                      const callStatus = e.target.value as number;
                      setSc({
                        ..._sc,
                        status: callStatus,
                      });
                      setRepairCompleted(callStatus >= 23);
                    }}
                  >
                    {SERVICE_CALL_STATUS_ENUM.map((e, i) => (
                      <MenuItem key={i} value={i}>
                        {e}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item sm={4}>
                  <InputLabel id="call-assignee">Assignee</InputLabel>
                  <Select
                    labelId="call-assignee"
                    value={_sc.assigned_to || "unassigned"}
                    fullWidth
                    size="small"
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        assigned_to: e.target.value,
                      });
                    }}
                  >
                    {usersWithUnassigned.map((i) => (
                      <MenuItem key={i.id} value={i.id}>
                        {i.name}
                      </MenuItem>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    {assignees.map((i) => {
                      // console.log(i.id, i.name); // Log each user object in the filtered list
                      return (
                        <MenuItem key={i.id} value={i.id}>
                          {i.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Grid>
                <Grid item sm={4}>
                  <InputLabel id="priority-level">Priority Level</InputLabel>
                  <Select
                    labelId="priority-level"
                    value={_sc.priority_level || 0}
                    fullWidth
                    size="small"
                    displayEmpty
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        priority_level: e.target.value as number,
                      });
                    }}
                  >
                    {/* Placeholder item */}
                    <MenuItem value={0} disabled>
                      <em>Unset</em>
                    </MenuItem>

                    <Divider sx={{ my: 1 }} />

                    {PRIORITY_LEVEL_ENUM.map((e) => (
                      <MenuItem key={e.value} value={e.value}>
                        {e.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item sm={4}>
                  <InputLabel id="priority-level">Service Date</InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    type="text"
                    autoComplete="off"
                    value={_sc.date_scheduled}
                    onBlur={(e) => {
                      const formatted = formatToISODate(e.target.value);
                      setSc({ ..._sc, date_scheduled: formatted });
                    }}
                    onChange={(e) => {
                      setSc({ ..._sc, date_scheduled: e.target.value });
                    }}
                    placeholder="YYYY-MM-DD"
                  />
                </Grid>
                <Grid item sm={4}>
                  <InputLabel id="appointment-time">
                    Appointment Time
                  </InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    type="text"
                    placeholder="HH:MM AM/PM"
                    value={_sc.appointment_time}
                    onChange={(e) => {
                      setSc({ ..._sc, appointment_time: e.target.value });
                    }}
                    inputProps={{
                      maxLength: 8,
                      pattern: "^(0[1-9]|1[0-2]):[0-5][0-9]\\s?(AM|PM)$",
                    }}
                  />
                </Grid>
                {/* <Grid item sm={4}></Grid> */}
                <Grid item sm={3}>
                  <FormControl sx={{ m: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          onChange={(e) => {
                            setSc({ ..._sc, is_callback: e.target.checked });
                          }}
                          checked={_sc.is_callback}
                        />
                      }
                      label="Callback?"
                    />
                  </FormControl>
                </Grid>
                <Grid item sm={3}>
                  <FormControl sx={{ m: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          onChange={(e) => {
                            setSc({ ..._sc, vip: e.target.checked });
                          }}
                          checked={_sc.vip}
                        />
                      }
                      label="VIP?"
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          {/* Tab In-Store Repair */}
          {/* {InStoreRepair && ( */}
          <TabPanel value={tabValue} index={repairIndex}>
            <Box
              component="form"
              sx={{
                "& > :not(style)": { mt: 1 },
              }}
              noValidate
              autoComplete="off"
            >
              <Grid container spacing={1} width={"100%"}>
                <Grid item sm={12}>
                  <InputLabel>Physical Condition</InputLabel>
                  <TextField
                    multiline
                    size="small"
                    rows={3}
                    fullWidth
                    disabled={repairCompleted}
                    value={_sc.physical_condition}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        physical_condition: e.target.value,
                      });
                    }}
                  />
                </Grid>

                <Grid item sm={12}>
                  <InputLabel id="initial-findings-label">
                    Initial Findings
                  </InputLabel>
                  <TextField
                    multiline
                    size="small"
                    rows={3}
                    fullWidth
                    // disabled={repairCompleted}
                    value={_sc.initial_findings}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        initial_findings: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>

                <Grid item sm={12}>
                  <InputLabel id="accessories-label">
                    Accessories Received
                  </InputLabel>

                  <Box
                    sx={{
                      border: "1px solid #d0d0d0",
                      borderRadius: 1,
                      p: 1,
                      maxHeight: 180,
                      overflowY: "auto",
                      mt: 1,
                    }}
                  >
                    <FormGroup>
                      {accessoryOptions.map((item) => {
                        const selected = _sc.accessories
                          ? _sc.accessories.split(",").map((x) => x.trim())
                          : [];

                        return (
                          <FormControlLabel
                            key={item}
                            control={
                              <Checkbox
                                size="small"
                                disabled={repairCompleted}
                                checked={selected.includes(item)}
                                onChange={() => handleAccessoryChange(item)}
                              />
                            }
                            label={item}
                          />
                        );
                      })}
                    </FormGroup>
                  </Box>
                </Grid>

                <Grid item sm={12}>
                  <InputLabel id="remarks-label">Remarks</InputLabel>
                  <TextField
                    multiline
                    size="small"
                    rows={3}
                    fullWidth
                    disabled={repairCompleted}
                    value={_sc.remarks}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        remarks: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>

                <Grid item sm={12}>
                  <InputLabel id="technical-diagnosis-label">
                    Technical Diagnosis
                  </InputLabel>
                  <TextField
                    multiline
                    size="small"
                    rows={3}
                    fullWidth
                    disabled={!repairCompleted}
                    value={_sc.technical_diagnosis}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        technical_diagnosis: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>

                <Grid item sm={12}>
                  <InputLabel id="parts-replaced-label">
                    Parts Replaced
                  </InputLabel>
                  <TextField
                    multiline
                    size="small"
                    rows={3}
                    fullWidth
                    disabled={!repairCompleted}
                    value={_sc.parts_replaced}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        parts_replaced: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>

                <Grid item sm={12}>
                  <InputLabel id="corrective-action-taken-label">
                    Corrective Action Taken
                  </InputLabel>
                  <TextField
                    multiline
                    size="small"
                    rows={3}
                    fullWidth
                    disabled={!repairCompleted}
                    value={_sc.corrective_action_taken}
                    onChange={(e) => {
                      setSc({
                        ..._sc,
                        corrective_action_taken: e.target.value,
                      });
                    }}
                  ></TextField>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          {/* )} */}
          <TabPanel value={tabValue} index={historyIndex}>
            <Box>
              <Grid container spacing={1} width={"100%"}>
                <Grid item sm={6}>
                  <Typography variant="h5">History</Typography>
                  <Divider />
                  <List>
                    {history.map((h) => (
                      <ListItem key={h.id}>
                        <ListItemText
                          primary={`${serviceCallLogActionDescription(
                            h.action,
                          )}: ${h.details}`}
                          secondary={`${UtcToPst(moment(h.created).toLocaleString())} - ${
                            h.user.name
                          }`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={activityIndex}>
            <MeterCountComponent
              callid={_sc.id || ""}
              counts={affectedMachine?.meter_counts || []}
            />

            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography>Items</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CallItemsControl
                  callId={_sc.id as string}
                  contract={machineContract}
                  division={_sc.division}
                />
              </AccordionDetails>
            </Accordion>

            <NotesComponent callid={_sc.id || ""} />
          </TabPanel>
        </DialogContent>
        <DialogActions>
          {/* added by alvin june2026 */}
          {InStoreRepair && !repairCompleted && (
            <>
              <Button
                color="success"
                disabled={isAdd}
                onClick={handlePrintAssessment}
              >
                Print Assessment Report
              </Button>
            </>
          )}
          {InStoreRepair && repairCompleted && (
            <>
              <Button
                color="success"
                disabled={isAdd}
                onClick={handlePrintRepair}
              >
                Print Repair Completion Report
              </Button>
            </>
          )}

          {!InStoreRepair && (
            <Button color="success" disabled={isAdd} onClick={handlePrintSWO}>
              Print Service Work Order
            </Button>
          )}

          <Button
            color="success"
            onClick={() => {
              console.log("Print Call Button Pressed");
              const serviceCall = _sc;
              const machine =
                data.machines?.find((m) => m.id === _sc.machine_id) ??
                emptyMachine(emptyCustomer().name);
              const customer =
                data.customers?.find((c) => c.id === machine?.customer_id) ??
                emptyCustomer();
              const assignee =
                data.users?.find((a) => a.id === _sc.assigned_to) ??
                emptyUser();
              printCallModalOpen(serviceCall, machine, customer, assignee);
            }}
            disabled={isAdd}
          >
            Print Call
          </Button>
          <Button
            disabled={loading}
            onClick={() => {
              if (isNullOrWhiteSpace(_sc.machine_id)) {
                alert("Please Select Machine");
                return;
              }

              if (isNullOrWhiteSpace(_sc.description)) {
                alert("Please add a description");
                return;
              }

              const isValid12HourTime = (time?: string) =>
                !time ||
                time.trim() === "" ||
                /^(0[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/i.test(time);

              if (!isValid12HourTime(_sc.appointment_time)) {
                alert(
                  "Please enter a valid time in 12-hour format or leave it blank.",
                );
                return;
              }

              // Handle the update here
              const updatedServiceCall = {
                ..._sc,
                assigned_to:
                  _sc.assigned_to === "unassigned" ? null : _sc.assigned_to,
              };

              setLoading(true);
              axios
                .post<ApiResponse<ServiceCall>>(
                  "calls/addorupdate",
                  JSON.stringify(updatedServiceCall),
                )
                .then((response) => {
                  const apiResponse = response.data;

                  if (apiResponse.success) {
                    showToast({
                      message: "Service Call saved!",
                      severity: "success",
                    });
                  } else {
                    showToast({
                      message: "An error occurred!",
                      severity: "error",
                    });
                  }
                })
                .catch((reason: AxiosError) => {
                  showToast({
                    message: `An Error occurred: ${reason.code}`,
                    severity: "error",
                  });
                })
                .finally(() => {
                  setLoading(false);
                  serviceCallModalClose();
                });
              // if the condition passed send an email to the original owner - found a perfect location
              // if(!isAdd && taskOwner !== _sc.assigned_to){
              //   console.log("An email notification has been sent to the previous Work Order owner.");
              // }
            }}
          >
            Save changes
          </Button>
        </DialogActions>
      </BootstrapDialog>
      <PrintCallModal onClose={data.fetchServiceCalls} />
    </div>
  );
}

export { serviceCallModalClose, serviceCallModalOpen };
